import React, { useEffect } from "react";
import { Box, Card, Typography } from "@mui/material";
import ClaimsStyles from "./ClaimsStyles";
import { activeClaims, pastClaims } from "../../../../constants/Constants";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  getClaimsData,
  GetClaimTransactions,
} from "../../../Dashboard/DashboardAction";
import ClaimCard from "../../../Dashboard/Claims/ClaimCard/ClaimCard";
import { RootState } from "../../../../store/store";
import {
  IClaim,
  IGetClaimTransactionsRequestParams,
} from "../../../../models/user/dashboard/Dashboard";
import CustomIcon from "../../../../common/CustomIcon/CustomIcon";
import { useTranslation } from "react-i18next";
import CustomSkeleton from "../../../../common/CustomSkeleton/CustomSkeleton";
import { useParams } from "react-router-dom";

const Claims: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const { isLoading, calledReducerType } = useAppSelector(
    (store: RootState) => store.common
  );
    const { customerId } = useParams();

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { claimsData } = useAppSelector((state: RootState) => state.dashboard);
  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );

  const handleClaimViewDetails = (
    claimId: string,
    carrier: string,
    system: string,
    lobCode: string
  ) => {
    const requestParams: IGetClaimTransactionsRequestParams = {
      claimId,
      system,
      carrier,
      lobCode,
    };
    dispatch(GetClaimTransactions(requestParams));
  };

  const getActiveClaims = () => {    
    if (claimsData && claimsData.claimList && claimsData.claimList.length > 0) {
      return claimsData.claimList
        ?.filter(
          (claim: IClaim) =>
            claim.carrierClaimStatus !== "Closed" &&
            claim.carrierPolicyNo === policyDetailsHeader?.carrierPolicyNumber
        )
        .map((claim: IClaim) => (
          <ClaimCard
            claim={claim}
            key={`policy-tab-claim-active-${claim.carrierClaimNo}`}
          />
        ));
    } else {
      return [];
    }
  };

  const getPastClaims = () => {
    if (claimsData && claimsData.claimList && claimsData.claimList.length > 0) {
      return claimsData.claimList
        ?.filter(
          (claim: IClaim) =>
            claim.carrierClaimStatus === "Closed" &&
            claim.carrierPolicyNo === policyDetailsHeader?.carrierPolicyNumber
        )
        .map((claim: IClaim) => (
          <ClaimCard
            claim={claim}
            key={`policy-tab-claim-past-${claim.carrierClaimNo}`}
          />
        ));
    } else {
      return [];
    }
  };

  useEffect(() => {    
    if (customerId) {
      dispatch(
        getClaimsData({
          customerId: customerId,
          carrier: "PRIS",
          system: "INOW",
        })
      );
    }
  }, [customerId, dispatch]);

  return (
    <>
      {isLoading && calledReducerType === "dashboard/getClaimsData" ? (
        <CustomSkeleton direction="column" height="120px" />
      ) : (
        <Box 
          aria-label="Claims Tab" 
          id="claims_container"
          data-test-id="claims_container"
          sx={ClaimsStyles.container}
        >
          <Box
            aria-label="Active Claims"
            id="active_claims_container"
            data-test-id="active_claims_container"
            sx={ClaimsStyles.activeClaimsContainer}
          >
            <Box 
              sx={ClaimsStyles.titleBox}
              id="active_claims_title"
              data-test-id="active_claims_title"
              aria-label="Active Claims Title"
            >
              <CustomIcon iconName="ActiveClaims" />
              <Typography sx={ClaimsStyles.titleText}>
                {t(activeClaims)}
              </Typography>
            </Box>
            {getActiveClaims().length > 0 ? (
              getActiveClaims()
            ) : (
              <Card 
                sx={ClaimsStyles.noClaimsCard}
                id="no_active_claims"
                data-test-id="no_active_claims"
                aria-label="No Active Claims Available"
              >
                <Typography sx={ClaimsStyles.noClaimsText}>
                  {t("No Claims Available")}
                </Typography>
              </Card>
            )}
          </Box>
          <Box
            aria-label="Past Claims"
            id="past_claims_container"
            data-test-id="past_claims_container"
            sx={ClaimsStyles.activeClaimsContainer}
          >
            <Box 
              sx={ClaimsStyles.titleBox}
              id="past_claims_title"
              data-test-id="past_claims_title"
              aria-label="Past Claims Title"
            >
              <CustomIcon iconName="PastClaims" />
              <Typography sx={ClaimsStyles.titleText}>
                {t(pastClaims)}
              </Typography>
            </Box>
            {getPastClaims().length > 0 ? (
              getPastClaims()
            ) : (
              <Card 
                sx={ClaimsStyles.noClaimsCard}
                id="no_past_claims"
                data-test-id="no_past_claims"
                aria-label="No Past Claims Available"
              >
                <Typography sx={ClaimsStyles.noClaimsText}>
                  {t("No Claims Available")}
                </Typography>
              </Card>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Claims;
